import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  Input,
  QueryList,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { IconBackdrop, IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureLoadMethod, PictureObjectFit } from '@freelancer/ui/picture';
import {
  FontColor,
  FontWeight,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { UsernameColor, UsernameSize } from '@freelancer/ui/username';

@Component({
  selector: 'fl-section-case-study-carousel-footer-header',
  template: `
    <p
      class="text-light text-small font-bold desktop:text-marketing_small"
      [flMarginBottom]="'xsmall'"
    >
      <ng-content></ng-content>
    </p>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselFooterHeaderComponent {
  FontColor = FontColor;
  FontWeight = FontWeight;
  TextSize = TextSize;
}

@Component({
  selector: 'fl-section-case-study-carousel-footer-sub-header',
  template: `
    <p class="text-light text-small desktop:text-marketing_small">
      <ng-content></ng-content>
    </p>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselFooterSubHeaderComponent {
  FontColor = FontColor;
  TextSize = TextSize;
}

@Component({
  selector: 'fl-section-case-study-carousel-footer',
  template: `
    <div class="SectionCaseStudyCarouselFooter">
      <div
        *ngIf="iconOffset"
        class="SectionCaseStudyCarouselIcon"
        [flMarginRight]="'mid'"
      ></div>
      <fl-picture
        class="SectionCaseStudyCarouselIcon"
        [src]="imageSrc"
        [boundedWidth]="true"
        [loadMethod]="PictureLoadMethod.LAZY"
        [flMarginRight]="'mid'"
        [attr.data-icon-only]="!headerComponent && !subHeaderComponent"
      ></fl-picture>
      <div
        class="SectionCaseStudyCarouselFooter-content"
        [flShowDesktop]="showDesktopOnly"
      >
        <ng-content
          select="fl-section-case-study-carousel-footer-header"
        ></ng-content>
        <ng-content
          select="fl-section-case-study-carousel-footer-sub-header"
        ></ng-content>
      </div>
    </div>
  `,
  styleUrls: [
    './section-case-study-carousel-footer.component.scss',
    './section-case-study-carousel-shared.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselFooterComponent {
  PictureLoadMethod = PictureLoadMethod;

  @Input() imageSrc: string;
  @Input() iconOffset = false;
  @Input() showDesktopOnly = true;

  @ContentChild(SectionCaseStudyCarouselFooterHeaderComponent)
  headerComponent: SectionCaseStudyCarouselHeaderComponent;
  @ContentChild(SectionCaseStudyCarouselFooterSubHeaderComponent)
  subHeaderComponent: SectionCaseStudyCarouselFooterSubHeaderComponent;
}

@Component({
  selector: 'fl-section-case-study-carousel-header',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselHeaderComponent {}

@Component({
  selector: 'fl-section-case-study-carousel-sub-header',
  template: `
    <p
      class="text-light text-marketing_small desktop:text-mid"
      [flMarginBottom]="'xxsmall'"
    >
      <ng-content></ng-content>
    </p>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselSubHeaderComponent {
  FontColor = FontColor;
  TextSize = TextSize;
}

@Component({
  selector: 'fl-section-case-study-carousel-header-detail',
  template: `
    <p
      class="text-color-inherit text-xsmall SectionCaseStudyCarouselHeaderDetail uppercase"
      [flMarginBottom]="'xlarge'"
    >
      <ng-content></ng-content>
    </p>
  `,
  styleUrls: ['./section-case-study-carousel-header-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselHeaderDetailComponent {
  FontColor = FontColor;
  TextSize = TextSize;
  TextTransform = TextTransform;
  PictureLoadMethod = PictureLoadMethod;
}

@Component({
  selector: 'fl-section-case-study-carousel-item',
  template: `
    <div class="SectionCaseStudyCarouselItem-container">
      <div
        class="SectionCaseStudyCarouselItem-contents"
        [flMarginBottomDesktop]="footerComponent ? 'xlarge' : 'none'"
      >
        <fl-picture
          *ngIf="isUserQuote || isRecruiterQuote"
          class="SectionCaseStudyCarouselIcon"
          alt="Quote"
          i18n-alt="Quotation mark icon alt text"
          [src]="'/seo-sem/case-study/quotation-mark.svg'"
          [boundedWidth]="true"
          [loadMethod]="PictureLoadMethod.LAZY"
          [flMarginRight]="'mid'"
          [attr.data-header-detail]="!!headerDetailComponent"
        ></fl-picture>

        <div>
          <ng-content
            select="fl-section-case-study-carousel-header-detail"
          ></ng-content>
          <fl-text
            [color]="FontColor.LIGHT"
            [size]="TextSize.LARGE"
            [sizeDesktop]="TextSize.XXLARGE"
            [weight]="FontWeight.MEDIUM"
            [flMarginBottom]="
              hideSubHeaderMobile && !userDisplayName ? 'none' : 'mid'
            "
            [flMarginBottomTablet]="'mid'"
            [flMarginBottomDesktop]="'large'"
          >
            <ng-content
              select="fl-section-case-study-carousel-header"
            ></ng-content>
          </fl-text>
          <fl-text
            *ngIf="subHeaderComponent"
            [flHideMobile]="hideSubHeaderMobile"
            [color]="FontColor.LIGHT"
            [size]="TextSize.MARKETING_SMALL"
            [sizeDesktop]="TextSize.MID"
            [flMarginBottom]="'xxsmall'"
          >
            <ng-content
              select="fl-section-case-study-carousel-sub-header"
            ></ng-content>
          </fl-text>

          <fl-username
            *ngIf="isUserQuote && userDisplayName && username"
            [color]="UsernameColor.LIGHT"
            [size]="UsernameSize.MID"
            [sizeDesktop]="UsernameSize.LARGE"
            [displayName]="userDisplayName"
            [username]="username"
          ></fl-username>
          <p
            class="text-light text-small desktop:text-marketing_small font-bold"
            i18n="Recruiter display name text"
            *ngIf="isRecruiterQuote && userDisplayName"
          >
            {{ userDisplayName }}, Recruiter
          </p>
        </div>
      </div>
      <div class="SectionCaseStudyCarouselItem-contents">
        <ng-content select="fl-section-case-study-carousel-footer"></ng-content>
      </div>
    </div>
    <div class="SectionCaseStudyCarouselItem-media">
      <fl-picture
        class="SectionCaseStudyCarouselItem-media-picture"
        [alt]="imageAlt"
        [src]="imageSrc"
        [width]="760"
        [srcTablet]="imageSrcTablet"
        [srcDesktop]="imageSrcDesktop"
        [srcLargeDesktop]="imageSrcLargeDesktop"
        [fixedAspectRatio]="true"
      ></fl-picture>
      <div
        class="SectionCaseStudyCarouselItem-caption"
        [flHideMobile]="true"
        [flHideTablet]="true"
        [flMarginBottom]="'xlarge'"
        [flMarginRight]="'xlarge'"
      >
        <ng-content select="fl-image-caption"></ng-content>
      </div>
    </div>
  `,
  styleUrls: [
    './section-case-study-carousel-item.component.scss',
    './section-case-study-carousel-shared.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselItemComponent {
  FontColor = FontColor;
  FontWeight = FontWeight;
  TextSize = TextSize;
  PictureObjectFit = PictureObjectFit;
  PictureLoadMethod = PictureLoadMethod;
  UsernameColor = UsernameColor;
  UsernameSize = UsernameSize;

  @Input() imageAlt: string;
  @Input() imageSrc: string;
  @Input() imageSrcTablet?: string;
  @Input() imageSrcDesktop?: string;
  @Input() imageSrcLargeDesktop?: string;
  @Input() isUserQuote = false;
  @Input() isRecruiterQuote = false;
  @Input() username: string;
  @Input() userDisplayName: string;
  @Input() hideSubHeaderMobile = false;

  @ContentChild(SectionCaseStudyCarouselHeaderDetailComponent)
  headerDetailComponent: SectionCaseStudyCarouselHeaderDetailComponent;
  @ContentChild(SectionCaseStudyCarouselSubHeaderComponent)
  subHeaderComponent: SectionCaseStudyCarouselSubHeaderComponent;
  @ContentChild(SectionCaseStudyCarouselFooterComponent)
  footerComponent: SectionCaseStudyCarouselFooterComponent;
}

@Component({
  selector: 'fl-section-case-study-carousel',
  template: `
    <fl-carousel
      class="SectionCaseStudyCarousel"
      [flAnimateOnScroll]="animateOnScroll"
      [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
      [showDotIndicator]="true"
      [autoplay]="true"
      [autoplayDuration]="AUTOPLAY_DURATION"
      [nextArrowBackdrop]="true"
      [arrowSize]="IconSize.SMALL"
      [arrowSizeTablet]="IconSize.MID"
      [arrowOffset]="ARROW_OFFSET_MOBILE"
      [arrowOffsetTablet]="ARROW_OFFSET_TABLET"
      [arrows]="showArrows"
      [arrowsTablet]="showArrowsTablet"
      [arrowsDesktop]="showArrowsDesktop"
    >
      <fl-carousel-item
        *ngFor="let carouselItem of carouselItems; trackBy: trackByItem"
      >
        <ng-container *ngTemplateOutlet="carouselItem"></ng-container>
      </fl-carousel-item>
    </fl-carousel>
  `,
  styleUrls: ['./section-case-study-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCaseStudyCarouselComponent {
  AnimationBehavior = AnimationBehavior;
  IconBackdrop = IconBackdrop;
  IconColor = IconColor;
  IconSize = IconSize;

  readonly ARROW_OFFSET_MOBILE = '180px';
  readonly ARROW_OFFSET_TABLET = '0px';
  readonly AUTOPLAY_DURATION = 15_000;

  @Input() animateOnScroll = false;
  @Input() showArrows = true;
  @Input() showArrowsTablet?: boolean;
  @Input() showArrowsDesktop?: boolean;

  @ContentChildren('carouselItem')
  carouselItems: QueryList<TemplateRef<SectionCaseStudyCarouselItemComponent>>;

  trackByItem(
    _: number,
    item: TemplateRef<SectionCaseStudyCarouselItemComponent>,
  ): TemplateRef<SectionCaseStudyCarouselItemComponent> {
    return item;
  }
}
